<template>
  <div class="trend-chart" :aria-label="label" role="img">
    <svg viewBox="0 0 640 180" preserveAspectRatio="none" aria-hidden="true">
      <line x1="0" x2="640" y1="150" y2="150" class="trend-chart__baseline" />
      <path v-if="linePath" :d="linePath" class="trend-chart__line" :style="{ stroke: color }" />
      <circle
        v-for="point in coordinates"
        :key="point.date"
        :cx="point.x"
        :cy="point.y"
        r="3"
        :fill="color"
      />
    </svg>
    <div class="trend-chart__labels">
      <span>{{ firstDate }}</span
      ><span>{{ lastDate }}</span>
    </div>
    <span class="sr-only">{{ summary }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
  label: string;
  points: Array<{ date: string; value: number }>;
  color: string;
}>();

const max = computed(() => Math.max(1, ...props.points.map((item) => item.value)));
const coordinates = computed(() =>
  props.points.map((item, index) => ({
    ...item,
    x: props.points.length <= 1 ? 320 : (index / (props.points.length - 1)) * 640,
    y: 150 - (item.value / max.value) * 122,
  })),
);
const linePath = computed(() =>
  coordinates.value.map((item, index) => `${index ? 'L' : 'M'} ${item.x} ${item.y}`).join(' '),
);
const firstDate = computed(() => shortDate(props.points.at(0)?.date));
const lastDate = computed(() => shortDate(props.points.at(-1)?.date));
const summary = computed(
  () => `${props.label}，最高 ${max.value}，共 ${props.points.length} 个日期点。`,
);

function shortDate(value?: string): string {
  return value ? value.slice(5) : '—';
}
</script>

<style scoped>
.trend-chart {
  min-width: 0;
}
.trend-chart svg {
  display: block;
  width: 100%;
  height: 178px;
  overflow: visible;
}
.trend-chart__baseline {
  stroke: var(--aw-border);
  stroke-width: 1;
}
.trend-chart__line {
  fill: none;
  stroke-width: 3;
  vector-effect: non-scaling-stroke;
}
.trend-chart__labels {
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  color: var(--aw-text-tertiary);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
